"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Box,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { importPassagensErp, getImportPassagensErpJob, cancelImportPassagensErpJob } from "../../api/client";

const POLL_MS = 1600;

function extractPayload(raw) {
  if (!raw || typeof raw !== "object") return null;
  if (raw.data !== undefined && raw.data !== null && typeof raw.data === "object") return raw.data;
  return raw;
}

export default function ImportPassagensErpDialog({ open, onClose, projectId, onCompleted }) {
  const { t } = useTranslation("products");
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [job, setJob] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const pollRef = useRef(null);
  const runIdRef = useRef(0);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const clearPoll = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) {
      clearPoll();
      return undefined;
    }

    const runId = ++runIdRef.current;

    setPhase("idle");
    setJob(null);
    setSyncResult(null);
    setError("");

    if (!projectId) {
      setPhase("failed");
      setError(t("toolbar.importPassagensErpNeedProject"));
      return undefined;
    }

    const pollOnce = async (jobId) => {
      try {
        const raw = await getImportPassagensErpJob(jobId);
        if (runId !== runIdRef.current) return;
        const j = extractPayload(raw) || raw;
        setJob(j);
        const st = String(j?.status || "");
        if (st === "completed" || st === "failed") {
          clearPoll();
          setPhase(st);
          if (st === "completed" && onCompletedRef.current) {
            await onCompletedRef.current();
          }
        }
      } catch (e) {
        if (runId !== runIdRef.current) return;
        const base =
          (e && typeof e === "object" && e.message) ||
          (typeof e === "string" ? e : null) ||
          t("importPassagensDialog.errorGeneric");
        const detail =
          e && typeof e === "object" && e.data && typeof e.data === "object" && e.data.detail != null
            ? String(e.data.detail).trim()
            : "";
        setError(detail ? `${base}\n\n${detail}` : base);
        clearPoll();
        setPhase("failed");
      }
    };

    const run = async () => {
      setPhase("starting");
      try {
        const raw = await importPassagensErp({ id_projeto: Number(projectId) });
        if (runId !== runIdRef.current) return;
        const payload = extractPayload(raw) || raw;

        if (payload?.accepted && payload.jobId) {
          setPhase("running");
          await pollOnce(payload.jobId);
          if (runId !== runIdRef.current) return;
          pollRef.current = setInterval(() => pollOnce(payload.jobId), POLL_MS);
        } else {
          setSyncResult(payload);
          setPhase("completed");
          if (onCompletedRef.current) await onCompletedRef.current();
        }
      } catch (e) {
        if (runId !== runIdRef.current) return;
        const base =
          (e && typeof e === "object" && e.message) ||
          (typeof e === "string" ? e : null) ||
          t("importPassagensDialog.errorGeneric");
        const detail =
          e && typeof e === "object" && e.data && typeof e.data === "object" && e.data.detail != null
            ? String(e.data.detail).trim()
            : "";
        setError(detail ? `${base}\n\n${detail}` : base);
        setPhase("failed");
      }
    };

    run();

    return () => {
      clearPoll();
    };
  }, [open, projectId, t]);

  const handleClose = () => {
    clearPoll();
    onClose?.();
  };

  const progressValue =
    job && job.overallProgress != null && Number.isFinite(Number(job.overallProgress))
      ? Math.min(100, Math.round(Number(job.overallProgress) * 100))
      : job && job.partnerProgress != null && Number.isFinite(Number(job.partnerProgress))
        ? Math.min(100, Math.round(Number(job.partnerProgress) * 100))
        : null;

  const createdAtMs = (() => {
    const s = job?.createdAt || job?.updatedAt || null;
    const t = s ? Date.parse(String(s)) : NaN;
    return Number.isFinite(t) ? t : null;
  })();
  const queuedForSec =
    String(job?.status || "") === "queued" && createdAtMs ? Math.floor((Date.now() - createdAtMs) / 1000) : null;
  const queuedLooksStuck = queuedForSec != null && queuedForSec >= 90;

  const canCancel =
    job &&
    (String(job.status) === "queued" || String(job.status) === "running" || String(job.status) === "cancel_requested") &&
    !job.cancelRequested;

  const handleCancel = async () => {
    const jid = String(job?.jobId || "").trim();
    if (!jid) return;
    try {
      await cancelImportPassagensErpJob(jid);
      setJob((prev) => (prev ? { ...prev, cancelRequested: true, status: prev.status === "queued" ? "cancel_requested" : prev.status } : prev));
    } catch (e) {
      const msg =
        (e && typeof e === "object" && e.message) || (typeof e === "string" ? e : null) || t("importPassagensDialog.cancelFailed");
      setError(msg);
    }
  };

  const showSyncNote = Boolean(syncResult) && syncResult.accepted === false;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={String(job?.status) === "running" || phase === "starting"}
    >
      <DialogTitle>{t("importPassagensDialog.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("importPassagensDialog.subtitle")}
        </Typography>

        {showSyncNote ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("importPassagensDialog.syncNote")}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {phase === "starting" ? <LinearProgress sx={{ mb: 2 }} /> : null}

        {String(job?.status) === "queued" ||
        String(job?.status) === "running" ||
        String(job?.status) === "cancel_requested" ||
        phase === "starting" ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              {String(job?.status || "") === "queued"
                ? t("importPassagensDialog.phaseQueued")
                : String(job?.status || "") === "cancel_requested"
                  ? t("importPassagensDialog.phaseCancelRequested")
                  : t("importPassagensDialog.phaseRunning")}
              {job?.partner ? ` · ${t("importPassagensDialog.partnerLine", { partner: job.partner })}` : ""}
              {queuedForSec != null ? ` · ${t("importPassagensDialog.queuedFor", { sec: queuedForSec })}` : ""}
            </Typography>
            {progressValue != null ? (
              <LinearProgress variant="determinate" value={progressValue} />
            ) : (
              <LinearProgress />
            )}
            {queuedLooksStuck ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {t("importPassagensDialog.queuedStuckHint")}
              </Alert>
            ) : null}
            {job?.trechosProcessed != null ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                {t("importPassagensDialog.statsLine", {
                  trechos: job.trechosProcessed ?? 0,
                  created: job.createdProducts ?? 0,
                  skipped: job.skippedProducts ?? 0,
                })}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {phase === "completed" && syncResult && syncResult.accepted === false ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("toolbar.importPassagensErpSuccessDetail", {
              trechos: syncResult.trechosProcessed ?? 0,
              created: syncResult.createdProducts ?? 0,
              skipped: syncResult.skippedProducts ?? 0,
            })}
          </Alert>
        ) : null}

        {phase === "completed" && job?.result ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t("toolbar.importPassagensErpSuccessDetail", {
              trechos: job.result.trechosProcessed ?? 0,
              created: job.result.createdProducts ?? 0,
              skipped: job.result.skippedProducts ?? 0,
            })}
          </Alert>
        ) : null}

        {phase === "failed" && job?.errorMessage ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {job.errorMessage}
          </Alert>
        ) : null}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("importPassagensDialog.progressLog")}
        </Typography>
        <List dense sx={{ maxHeight: 280, overflow: "auto", bgcolor: "action.hover", borderRadius: 1 }}>
          {(job?.log && job.log.length ? job.log : syncResult?.message ? [syncResult.message] : []).map((line, i) => (
            <ListItem key={i} disablePadding sx={{ px: 1, py: 0.25 }}>
              <ListItemText primary={line} primaryTypographyProps={{ variant: "body2" }} />
            </ListItem>
          ))}
          {(!job?.log || job.log.length === 0) && !syncResult?.message && phase === "starting" ? (
            <ListItem>
              <ListItemText primary="…" />
            </ListItem>
          ) : null}
        </List>
      </DialogContent>
      <DialogActions>
        {canCancel ? (
          <Button color="error" onClick={handleCancel}>
            {t("importPassagensDialog.cancel")}
          </Button>
        ) : null}
        <Button onClick={handleClose}>{t("importPassagensDialog.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
